import React, { useContext, useState } from 'react';
import { useLoader } from '../api/useLoader';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import Spinner from '../components/spinner/spinner';
import {
  PraksisplassTilFordelingDto,
  PraksisstedTilFordelingDto,
  StudentfordelingMedValgDto,
} from '../api/generated/studiested-generated-api';
import PanelHeader from '../components/panelheader/panelHeader';
import { useIntl } from 'react-intl';
import translations from './oppsummering.uha.i18n';
import { Grid } from '@mui/material';
import { NavLink } from 'react-router-dom';
import {
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import {
  OnStedHoverCard,
  StedType,
} from '../components/hierarchy/onhoverhierarchyinfo';
import * as css from './oppsummering.module.scss';
import { useActivityContextUHA } from './activitycontexts';
import { useFormatMessageFor } from '../i18n/translation';

interface RowData {
  sted: PraksisstedTilFordelingDto;
  plass: PraksisplassTilFordelingDto;
  studenter: number;
}

const Oppsummering = () => {
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const { praksisaktivitet } = useActivityContextUHA();
  const { id: aktivitetsId } = praksisaktivitet;

  const { data, loading } = useLoader(
    () =>
      praksiskalenderApi.praksisaktivitetIdFordelingGet({
        pathParams: { id: aktivitetsId },
      }),
    [aktivitetsId],
  );

  return (
    <>
      {loading && <Spinner />}
      {data && <Oversikt fordeling={data} />}
    </>
  );
};

const Oversikt: React.FC<
  React.PropsWithChildren<{
    fordeling: StudentfordelingMedValgDto;
  }>
> = ({ fordeling }) => {
  const [activeCol, setActiveCol] = useState<number>(-1);
  const [sortedData, setSortedData] = useState<RowData[]>([]);
  const [search, setSearch] = useState<string[]>(['', '']);

  const { locale } = useIntl();
  const labels = useFormatMessageFor(translations);

  const headers = [
    { title: labels.praksissted },
    { title: labels.status },
    { title: labels.studenter },
    { title: labels.kapasitet },
  ];

  const getRowsData = React.useCallback(() => {
    const rows: RowData[] = [];

    fordeling.muligePraksissteder.forEach((sted) => {
      sted.muligePraksisplasser.forEach((plass) => {
        const studenter = fordeling.studentfordeling.filter(
          (fordeling) =>
            fordeling.praksisplass?.praksissted.id === plass.praksissted.id,
        ).length;

        rows.push({ sted, plass, studenter });
      });
    });

    return rows;
  }, [fordeling]);

  const filteredAndSortedData = React.useMemo(
    () =>
      (sortedData.length > 0 ? sortedData : getRowsData()).filter((row) => {
        const { navn: topName } = row.sted.praksissted;
        const { navn: leafName } = row.plass.praksissted;
        return (
          search[0].length === 0 ||
          `${topName} ${leafName}`.toLowerCase().indexOf(search[0]) > -1
        );
      }),
    [sortedData, search, getRowsData],
  );

  const getRows = () =>
    filteredAndSortedData.map(({ plass, sted, studenter }) => (
      <OversiktTableRow
        key={plass.praksissted.id}
        sted={sted.praksissted.navn}
        avdelingId={plass.praksissted.id}
        avdeling={plass.praksissted.navn}
        studenter={studenter}
        kapasitet={plass.kapasitet}
        alert={plass.hasValideringsfeil}
        headers={headers}
        id={plass.praksissted.id}
      />
    ));

  const sort = (col: number, a: RowData, b: RowData) => {
    switch (col) {
      case 0:
        return compare(
          `${a.sted.praksissted.navn} ${a.plass.praksissted.navn}`,
          `${b.sted.praksissted.navn} ${b.plass.praksissted.navn}`,
        );
      case 1:
        return compare(
          a.studenter < b.plass.kapasitet ? labels.ledig : labels.fylt,
          b.studenter < b.plass.kapasitet ? labels.ledig : labels.fylt,
        );
      case 2:
        return compare(a.studenter.toString(), b.studenter.toString());
      case 3:
        return compare(
          a.plass.kapasitet.toString(),
          b.plass.kapasitet.toString(),
        );
    }

    return 0;
  };

  const compare = Intl.Collator(locale || 'nb').compare;
  const sortOppsummeringByActiveCol = (col: number, ascending: boolean) =>
    setSortedData(
      getRowsData().sort((a, b) =>
        ascending ? sort(col, b, a) : sort(col, a, b),
      ),
    );

  const canSearchIndices = [0];

  const getHeaders = () =>
    headers.map(({ title }, index) => (
      <TableCellTh
        key={title}
        colIndex={index}
        activeCol={index === activeCol}
        setActiveCol={setActiveCol}
        sort={sortOppsummeringByActiveCol}
        canSort
        canSearch={canSearchIndices.includes(index)}
        search={(col, value) =>
          setSearch(
            search.map((searchString, i) =>
              i === col ? value.toLowerCase() : searchString,
            ),
          )
        }
      >
        {title}
      </TableCellTh>
    ));

  return (
    <>
      <PanelHeader title={labels.oversikt} />
      <Grid container>
        <Grid item xs={12}>
          <Table>
            <TableHeader>
              <TableRow top>{getHeaders()}</TableRow>
            </TableHeader>
            <TableMain>{getRows()}</TableMain>
          </Table>
        </Grid>
      </Grid>
    </>
  );
};

const OversiktTableRow: React.FC<
  React.PropsWithChildren<{
    sted: string;
    avdelingId: string;
    avdeling: string;
    studenter: number;
    kapasitet: number;
    alert: boolean;
    headers: { title: string }[];
    id?: string;
  }>
> = ({
  sted,
  avdelingId,
  avdeling,
  studenter,
  kapasitet,
  alert,
  headers,
  id,
}) => {
  const labels = useFormatMessageFor(translations);

  let tabHeader = 0;
  return (
    <TableRow rowBackground={alert ? 'alert' : undefined} id={id}>
      <TableCellTd header={headers[tabHeader++].title}>
        <div className={css.practiceplaceName}>
          <span>{`${sted} -`}</span>
          <NavLink to={`/practiceplaces/profile/${avdelingId}`}>
            {avdeling}
          </NavLink>
          <OnStedHoverCard
            id={avdelingId}
            navn={avdeling}
            sted={StedType.PRAKSISSTED}
          />
        </div>
      </TableCellTd>
      <TableCellTd header={headers[tabHeader++].title}>
        {studenter < kapasitet ? labels.ledig : labels.fylt}
      </TableCellTd>
      <TableCellTd header={headers[tabHeader++].title}>{studenter}</TableCellTd>
      <TableCellTd header={headers[tabHeader].title}>{kapasitet}</TableCellTd>
    </TableRow>
  );
};

export default Oppsummering;
